import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Footer, Modal, FileUploadZone } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import ProfileIdentity from './ProfileIdentity';
import BioEditor from './BioEditor';
import ConnectedPlatforms from './ConnectedPlatforms';
import FollowersPopup from './FollowersPopup';
import MediaGallery from './MediaGallery';
import LiveStreamBlock from './LiveStreamBlock';
import ImageCropper from './ImageCropper';
import { ReactComponent as BannerUploadIcon } from '../../assets/icons/banner-upload.svg';
import { ReactComponent as BannerChangeIcon } from '../../assets/icons/banner-change.svg';
import { ReactComponent as IntroVideoUploadIcon } from '../../assets/icons/intro-video-upload.svg';
import { ReactComponent as IntroVideoRemoveIcon } from '../../assets/icons/intro-video-remove.svg';
import { ReactComponent as EditProfileIcon } from '../../assets/icons/edit-profile.svg';
import { ReactComponent as SaveChangesIcon } from '../../assets/icons/save-changes.svg';
import { ReactComponent as CancelIcon } from '../../assets/icons/cancel-edit.svg';
import { ROUTES } from '../../constants';
import {
  FOLLOW_GRAPH_UPDATED_EVENT,
  authApi,
  clearStoredAuth,
  readStoredToken,
} from '../../services/authApi';
import streamApi from '../../services/streamApi';
import { readStoredUser, syncStoredUser, useNavbarUser } from '../../utils/navbarUser';
import { hasDirectAdminAccess } from '../../utils/adminAccess';
import './ProfilePage.css';

const MAX_VIDEO_DURATION_SECONDS = 180;

function isBlobUrl(value) {
  return typeof value === 'string' && value.startsWith('blob:');
}

function revokeObjectUrl(value) {
  if (isBlobUrl(value)) {
    URL.revokeObjectURL(value);
  }
}

function buildProfileState(user) {
  const profileImage =
    user?.profileImage && !user.profileImage.startsWith('blob:')
      ? user.profileImage
      : null;

  return {
    id: user?.id || null,
    bannerUrl: null,
    avatarUrl: profileImage,
    username: user?.username || user?.firstName || 'StreamerPro',
    bio: '',
    connectedAccounts: [],
    videoUrl: null,
    followersCount: 0,
    followingCount: 0,
    mediaVisibility: 'followers',
    isPro: false,
  };
}

function formatJoinedDate(createdAt) {
  if (!createdAt) return 'today';
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return 'today';
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function mapConnectedAccounts(connectedPlatforms = []) {
  return (connectedPlatforms || []).map((platform) => ({
    platformId: platform.platform,
    username: platform.platformUsername,
    profileUrl: platform.profileUrl || null,
  }));
}

function ProfilePage() {
  const storedUser = readStoredUser();
  const user = useNavbarUser();
  const heroVideoRef = useRef(null);
  const profileSnapshotRef = useRef(null);
  const connectedPlatformsRef = useRef(null);

  const [profile, setProfile] = useState(() => buildProfileState(storedUser));
  const [joinedDate, setJoinedDate] = useState(() => formatJoinedDate(storedUser?.createdAt));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [logoRoute] = useState(() => (hasDirectAdminAccess() ? ROUTES.ADMIN : ROUTES.HOME));

  const [uploadingMedia, setUploadingMedia] = useState({ avatar: false, banner: false, introVideo: false });
  const [showFollowersPopup, setShowFollowersPopup] = useState(false);
  const [followersPopupTab, setFollowersPopupTab] = useState('followers');
  const [liveStatus, setLiveStatus] = useState(null);

  /* Edit mode */
  const [isEditMode, setIsEditMode] = useState(false);

  /* Hero video state */
  const [isHeroPlaying, setIsHeroPlaying] = useState(true);
  const [isHeroMuted, setIsHeroMuted] = useState(true);
  const [isHeroHovering, setIsHeroHovering] = useState(false);
  const [isDraggingVideo, setIsDraggingVideo] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState('');

  /* Banner crop modal state */
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [tempBannerFile, setTempBannerFile] = useState(null);
  const [tempBannerUrl, setTempBannerUrl] = useState(null);
  const [showBannerCropper, setShowBannerCropper] = useState(false);

  const setMediaUploading = useCallback((kind, value) => {
    setUploadingMedia((prev) => ({ ...prev, [kind]: value }));
  }, []);

  /* ── Profile Load ──────────────────────────────────────────────────────── */
  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadProfile = async () => {
      try {
        const [profileResponse, introResponse, platformStatusResponse] = await Promise.all([
          authApi.getProfile(token).catch(() => ({ profile: null })),
          authApi.getIntroVideo(token).catch(() => ({ introVideo: null })),
          authApi.getPlatformPlanStatus(token).catch(() => ({ is_subscribed: false })),
        ]);

        if (cancelled) return;

        const profileData = profileResponse?.profile || {};
        const connectedAccounts = mapConnectedAccounts(profileData.connectedPlatforms);
        const persistedBannerUrl =
          profileData.bannerUrl && !profileData.bannerUrl.startsWith('blob:')
            ? profileData.bannerUrl
            : null;
        const persistedProfileImage =
          profileData.profilePictureUrl && !profileData.profilePictureUrl.startsWith('blob:')
            ? profileData.profilePictureUrl
            : null;
        const storedProfileImage =
          storedUser?.profileImage && !storedUser.profileImage.startsWith('blob:')
            ? storedUser.profileImage
            : null;
        const nextAvatar = persistedProfileImage || profileData.oauthProfileImage || storedProfileImage || null;

        const publicProfileResponse = profileData.username
          ? await authApi.getPublicProfile(profileData.username).catch(() => ({ profile: null }))
          : { profile: null };

        setProfile((prev) => ({
          ...prev,
          id: profileData.id || prev.id,
          bannerUrl: persistedBannerUrl,
          avatarUrl: nextAvatar,
          username: profileData.username || profileData.firstName || prev.username,
          bio: profileData.bio || '',
          connectedAccounts,
          videoUrl: introResponse?.introVideo?.s3Link || null,
          followersCount: publicProfileResponse?.profile?.followersCount || 0,
          followingCount: publicProfileResponse?.profile?.followingCount || 0,
          mediaVisibility: profileData.mediaVisibility || 'followers',
          isPro: Boolean(platformStatusResponse?.is_subscribed || publicProfileResponse?.profile?.isPro),
          oauthProvider: profileData.oauthProvider || null,
          oauthProviderUsername: profileData.oauthProviderUsername || null,
        }));
        setJoinedDate(profileData.joinedDate || formatJoinedDate(storedUser?.createdAt));
        syncStoredUser({
          username: profileData.username || storedUser?.username,
          firstName: profileData.firstName || storedUser?.firstName,
          lastName: profileData.lastName || storedUser?.lastName,
          profileImage: nextAvatar,
          createdAt: storedUser?.createdAt,
        });
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProfile();
    return () => { cancelled = true; };
  }, [storedUser?.createdAt, storedUser?.firstName, storedUser?.lastName, storedUser?.profileImage, storedUser?.username]);

  /* ── Follow graph live refresh ─────────────────────────────────────────── */
  useEffect(() => {
    const currentUsername = (profile.username || '').trim();
    if (!currentUsername) return undefined;

    let cancelled = false;

    const refreshSocialCounts = async () => {
      try {
        const res = await authApi.getPublicProfile(currentUsername);
        if (cancelled) return;
        setProfile((prev) => ({
          ...prev,
          followersCount: res?.profile?.followersCount || 0,
          followingCount: res?.profile?.followingCount || 0,
        }));
      } catch { /* silent */ }
    };

    const handleFollowGraphUpdated = () => { refreshSocialCounts(); };
    window.addEventListener(FOLLOW_GRAPH_UPDATED_EVENT, handleFollowGraphUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener(FOLLOW_GRAPH_UPDATED_EVENT, handleFollowGraphUpdated);
    };
  }, [profile.username]);

  /* ── Live status ───────────────────────────────────────────────────────── */
  useEffect(() => {
    const profileId = profile?.id;
    if (!profileId) { setLiveStatus(null); return undefined; }

    let cancelled = false;

    const fetchLive = async () => {
      try {
        const statuses = await streamApi.getLiveStatuses([profileId]);
        if (cancelled) return;
        const entry = Array.isArray(statuses)
          ? statuses.find((s) => String(s.user_id) === String(profileId))
          : null;
        setLiveStatus(entry && entry.is_live ? entry : null);
      } catch {
        if (!cancelled) setLiveStatus(null);
      }
    };

    fetchLive();
    const interval = setInterval(fetchLive, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [profile.id]);

  /* ── Banner handlers ───────────────────────────────────────────────────── */
  const handleBannerFile = useCallback(async (file) => {
    const previewUrl = URL.createObjectURL(file);
    setMediaUploading('banner', true);
    setError(null);
    setProfile((prev) => ({ ...prev, bannerUrl: previewUrl }));
    try {
      const uploadData = await authApi.uploadProfileMedia({ kind: 'banner', file });
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, bannerUrl: uploadData.fileUrl }));
    } catch (saveError) {
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, bannerUrl: profileSnapshotRef.current?.bannerUrl ?? null }));
      setError(saveError.message || 'Failed to upload banner');
    } finally {
      setMediaUploading('banner', false);
    }
  }, [setMediaUploading]);

  /* Banner modal */
  const handleBannerFileSelect = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setTempBannerFile(file);
    setTempBannerUrl(url);
    setShowBannerCropper(true);
    setIsBannerModalOpen(true);
  }, []);

  const handleBannerInputChange = useCallback((e) => {
    handleBannerFileSelect(e.target.files[0]);
  }, [handleBannerFileSelect]);

  const handleBannerCropCancel = useCallback(() => {
    if (tempBannerUrl) URL.revokeObjectURL(tempBannerUrl);
    setTempBannerFile(null);
    setTempBannerUrl(null);
    setShowBannerCropper(false);
  }, [tempBannerUrl]);

  const handleBannerModalClose = useCallback(() => {
    handleBannerCropCancel();
    setIsBannerModalOpen(false);
  }, [handleBannerCropCancel]);

  const handleBannerCropComplete = useCallback(async (croppedFile) => {
    const fileToUpload = croppedFile || tempBannerFile;
    if (!fileToUpload) return;
    try {
      await handleBannerFile(fileToUpload);
    } finally {
      if (tempBannerUrl) URL.revokeObjectURL(tempBannerUrl);
      setTempBannerFile(null);
      setTempBannerUrl(null);
      setShowBannerCropper(false);
      setIsBannerModalOpen(false);
    }
  }, [handleBannerFile, tempBannerFile, tempBannerUrl]);

  /* ── Avatar handler ────────────────────────────────────────────────────── */
  const handleAvatarFile = useCallback(async (file) => {
    const previousUrl = profile.avatarUrl;
    const previewUrl = URL.createObjectURL(file);
    setMediaUploading('avatar', true);
    setError(null);
    setProfile((prev) => ({ ...prev, avatarUrl: previewUrl }));
    try {
      const uploadData = await authApi.uploadProfileMedia({ kind: 'avatar', file });
      await authApi.updateProfile({ profilePictureUrl: uploadData.fileUrl });
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, avatarUrl: uploadData.fileUrl }));
      syncStoredUser({ profileImage: uploadData.fileUrl });
    } catch (saveError) {
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, avatarUrl: previousUrl }));
      setError(saveError.message || 'Failed to upload avatar');
    } finally {
      setMediaUploading('avatar', false);
    }
  }, [profile.avatarUrl, setMediaUploading]);

  /* ── Bio handler — deferred until Save Changes ─────────────────────────── */
  const handleBioChange = useCallback((bio) => {
    setProfile((prev) => ({ ...prev, bio }));
  }, []);

  /* ── Media visibility handler ──────────────────────────────────────────── */
  const handleMediaVisibilityChange = useCallback(async (nextValue) => {
    if (nextValue !== 'followers' && nextValue !== 'private') return;
    const previousValue = profile.mediaVisibility;
    setProfile((prev) => ({ ...prev, mediaVisibility: nextValue }));
    try {
      await authApi.updateProfile({ mediaVisibility: nextValue });
    } catch {
      setProfile((prev) => ({ ...prev, mediaVisibility: previousValue }));
    }
  }, [profile.mediaVisibility]);

  /* ── Platform handlers ─────────────────────────────────────────────────── */
  const handleAddAccount = useCallback(async ({ platformId, username }) => {
    try {
      const data = await authApi.addPlatform({ platform: platformId, platformUsername: username });
      setProfile((prev) => ({ ...prev, connectedAccounts: mapConnectedAccounts(data.profile?.connectedPlatforms) }));
    } catch { /* silent */ }
  }, []);

  const handleRemoveAccount = useCallback(async (platformId) => {
    try {
      const data = await authApi.removePlatform(platformId);
      setProfile((prev) => ({ ...prev, connectedAccounts: mapConnectedAccounts(data.profile?.connectedPlatforms) }));
    } catch { /* silent */ }
  }, []);

  /* ── Video handlers — deferred until Save Changes ──────────────────────── */
  const handleVideoChange = useCallback((url) => {
    setProfile((prev) => ({ ...prev, videoUrl: url }));
  }, []);

  const handleRemoveBanner = useCallback(() => {
    setProfile((prev) => {
      if (isBlobUrl(prev.bannerUrl)) revokeObjectUrl(prev.bannerUrl);
      return { ...prev, bannerUrl: null };
    });
  }, []);

  const handleVideoFile = useCallback(async (file, previewUrl) => {
    setMediaUploading('introVideo', true);
    setError(null);
    try {
      const uploadData = await authApi.uploadProfileMedia({ kind: 'intro-video', file });
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, videoUrl: uploadData.fileUrl }));
    } catch (saveError) {
      revokeObjectUrl(previewUrl);
      setProfile((prev) => ({ ...prev, videoUrl: profileSnapshotRef.current?.videoUrl ?? null }));
      setError(saveError.message || 'Failed to upload intro video');
    } finally {
      setMediaUploading('introVideo', false);
    }
  }, [setMediaUploading]);

  const validateVideoDuration = useCallback((file) => (
    new Promise((resolve, reject) => {
      const previewUrl = URL.createObjectURL(file);
      const probeVideo = document.createElement('video');
      probeVideo.preload = 'metadata';
      const cleanup = () => { URL.revokeObjectURL(previewUrl); probeVideo.removeAttribute('src'); probeVideo.load(); };
      probeVideo.onloadedmetadata = () => {
        const duration = Number(probeVideo.duration);
        cleanup();
        if (!Number.isFinite(duration)) { reject(new Error('Could not verify intro video length')); return; }
        if (duration > MAX_VIDEO_DURATION_SECONDS) { reject(new Error('Intro video must be 3 minutes or shorter')); return; }
        resolve();
      };
      probeVideo.onerror = () => { cleanup(); reject(new Error('Failed to read intro video metadata')); };
      probeVideo.src = previewUrl;
    })
  ), []);

  const handleVideoInputChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVideoUploadError('');
    try {
      await validateVideoDuration(file);
      const previewUrl = URL.createObjectURL(file);
      handleVideoChange(previewUrl);
      handleVideoFile(file, previewUrl);
    } catch (err) {
      setVideoUploadError(err.message);
    }
  }, [handleVideoChange, handleVideoFile, validateVideoDuration]);

  const handleVideoDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDraggingVideo(false);
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('video/')) return;
    setVideoUploadError('');
    try {
      await validateVideoDuration(file);
      const previewUrl = URL.createObjectURL(file);
      handleVideoChange(previewUrl);
      handleVideoFile(file, previewUrl);
    } catch (err) {
      setVideoUploadError(err.message);
    }
  }, [handleVideoChange, handleVideoFile, validateVideoDuration]);

  /* ── Edit mode handlers ────────────────────────────────────────────────── */
  const enterEditMode = useCallback(() => {
    profileSnapshotRef.current = { ...profile };
    setIsEditMode(true);
  }, [profile]);

  const handleCancelEdit = useCallback(() => {
    const snap = profileSnapshotRef.current;
    if (snap) {
      setProfile((current) => {
        if (isBlobUrl(current.bannerUrl) && current.bannerUrl !== snap.bannerUrl) {
          revokeObjectUrl(current.bannerUrl);
        }
        if (isBlobUrl(current.videoUrl) && current.videoUrl !== snap.videoUrl) {
          revokeObjectUrl(current.videoUrl);
        }
        return { ...snap };
      });
    }
    setIsEditMode(false);
    profileSnapshotRef.current = null;
  }, []);

  const handleSaveChanges = useCallback(async () => {
    setSaving(true);
    const snap = profileSnapshotRef.current;
    try {
      await authApi.updateProfile({
        bio: profile.bio,
        profilePictureUrl: profile.avatarUrl,
        bannerUrl: profile.bannerUrl,
      });
      const videoChanged = profile.videoUrl !== snap?.videoUrl;
      if (videoChanged) {
        if (!profile.videoUrl) {
          await authApi.updateIntroVideo({ s3Link: null, processingStatus: 'removed' }).catch(() => {});
        } else if (!isBlobUrl(profile.videoUrl)) {
          await authApi.updateIntroVideo({ s3Link: profile.videoUrl, processingStatus: 'uploaded' }).catch(() => {});
        }
      }
      await connectedPlatformsRef.current?.commitChanges();
    } catch { /* silent */ }
    finally {
      setSaving(false);
      setIsEditMode(false);
      profileSnapshotRef.current = null;
    }
  }, [profile.avatarUrl, profile.bannerUrl, profile.bio, profile.videoUrl]);

  const handleLogout = useCallback(async () => {
    const token = readStoredToken();
    await authApi.logout(token);
    clearStoredAuth();
    window.location.assign(ROUTES.WELCOME);
  }, []);

  /* ── Render ────────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="profile-page profile-page--loading">
        <p style={{ color: '#7a8fb5', textAlign: 'center', paddingTop: '20vh' }}>Loading profile…</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={profile.avatarUrl}
        userName={profile.username}
        onSpadesClick={() => {}}
        onLogout={handleLogout}
        logoRoute={logoRoute}
        minimal
        forceDocumentNavigation
      />

      <LeftSidebar forceDocumentNavigation />

      <main className="profile-page__main">
        {error && (
          <div className="profile-page__error-bar">{error}</div>
        )}

        {liveStatus && (
          <LiveStreamBlock
            profile={{
              id: profile.id,
              username: profile.username,
              oauthProvider: profile.oauthProvider,
              oauthProviderUsername: profile.oauthProviderUsername,
              connectedPlatforms: (profile.connectedAccounts || []).map((a) => ({
                platform: a.platformId,
                platformUsername: a.username,
              })),
            }}
            liveStatus={liveStatus}
          />
        )}

        {/* ── Hero: Banner + Intro Video ──────────────────────────────────── */}
        <div
          className={`profile-hero${isEditMode ? ' profile-hero--editing' : ''}`}
          onMouseEnter={() => setIsHeroHovering(true)}
          onMouseLeave={() => setIsHeroHovering(false)}
        >
          {/* Banner background */}
          {profile.bannerUrl ? (
            <img
              src={profile.bannerUrl}
              alt=""
              className="profile-hero__banner-img"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="profile-hero__banner-gradient" />
          )}

          <div className="profile-hero__overlay" />

          {/* Edit mode: remove banner X button */}
          {isEditMode && profile.bannerUrl && (
            <button
              type="button"
              className="profile-hero__remove-banner-btn"
              onClick={handleRemoveBanner}
              disabled={uploadingMedia.banner}
              aria-label="Remove banner"
            >
              <IntroVideoRemoveIcon />
            </button>
          )}

          {/* Intro video or upload zone */}
          <div className="profile-hero__video-area">
            {profile.videoUrl ? (
              <div className="profile-hero__video-container">
                <video
                  ref={heroVideoRef}
                  src={profile.videoUrl}
                  className="profile-hero__video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  onPlay={() => setIsHeroPlaying(true)}
                  onPause={() => setIsHeroPlaying(false)}
                />

                <button
                  type="button"
                  className={`profile-hero__play-btn${isHeroHovering ? ' profile-hero__play-btn--visible' : ''}`}
                  aria-label={isHeroPlaying ? 'Pause intro video' : 'Play intro video'}
                  onClick={() => {
                    if (!heroVideoRef.current) return;
                    if (heroVideoRef.current.paused) heroVideoRef.current.play();
                    else heroVideoRef.current.pause();
                  }}
                >
                  {isHeroPlaying ? (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  ) : (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="6 3 20 12 6 21 6 3" />
                    </svg>
                  )}
                </button>

                <button
                  type="button"
                  className={`profile-hero__mute-btn${isHeroHovering ? ' profile-hero__mute-btn--visible' : ''}`}
                  aria-label={isHeroMuted ? 'Unmute intro video' : 'Mute intro video'}
                  onClick={() => {
                    if (!heroVideoRef.current) return;
                    heroVideoRef.current.muted = !heroVideoRef.current.muted;
                    setIsHeroMuted(heroVideoRef.current.muted);
                  }}
                >
                  {isHeroMuted ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11 5L6 9H2v6h4l5 4V5z" />
                      <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11 5L6 9H2v6h4l5 4V5z" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                    </svg>
                  )}
                </button>

                {isEditMode && (
                  <button
                    type="button"
                    className="profile-hero__remove-video-btn"
                    onClick={() => handleVideoChange(null)}
                    disabled={uploadingMedia.introVideo}
                    aria-label="Remove intro video"
                  >
                    <IntroVideoRemoveIcon />
                  </button>
                )}
              </div>
            ) : isEditMode ? (
              <div
                className={`profile-hero__video-empty${isDraggingVideo ? ' profile-hero__video-empty--dragging' : ''}`}
                onDragEnter={(e) => { e.preventDefault(); setIsDraggingVideo(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDraggingVideo(false); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleVideoDrop}
              >
                <label className="profile-hero__video-empty-label">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoInputChange}
                    className="profile-hero__hidden-input"
                    disabled={uploadingMedia.introVideo}
                  />
                  {uploadingMedia.introVideo ? (
                    <span className="profile-hero__video-uploading">Uploading…</span>
                  ) : (
                    <IntroVideoUploadIcon className="profile-hero__video-upload-icon" />
                  )}
                </label>
                {videoUploadError && (
                  <div className="profile-hero__video-error">{videoUploadError}</div>
                )}
              </div>
            ) : null}
          </div>

          {/* Edit mode: change banner button */}
          {isEditMode && (
            <label className="profile-hero__change-banner-btn" aria-label="Change banner">
              <input
                type="file"
                accept="image/*"
                onChange={handleBannerInputChange}
                className="profile-hero__hidden-input"
                disabled={uploadingMedia.banner}
              />
              {uploadingMedia.banner ? (
                <span className="profile-hero__banner-saving">Saving…</span>
              ) : profile.bannerUrl ? (
                <BannerChangeIcon className="profile-hero__banner-change-svg" />
              ) : (
                <BannerUploadIcon className="profile-hero__banner-upload-svg" />
              )}
            </label>
          )}
        </div>

        {/* ── Content below hero ────────────────────────────────────────── */}
        <div className="profile-page__content">

          {/* Identity + edit controls */}
          <section className="profile-page__section profile-page__section--identity">
            <ProfileIdentity
              avatarUrl={profile.avatarUrl}
              onAvatarFile={handleAvatarFile}
              username={profile.username}
              isPro={Boolean(profile.isPro)}
              joinedDate={joinedDate}
              followers={profile.followersCount || 0}
              following={profile.followingCount || 0}
              uploading={uploadingMedia.avatar}
              editMode={isEditMode}
              onFollowersClick={profile.username ? () => { setFollowersPopupTab('followers'); setShowFollowersPopup(true); } : null}
              onFollowingClick={profile.username ? () => { setFollowersPopupTab('following'); setShowFollowersPopup(true); } : null}
            />

            <div className="profile-page__edit-controls">
                {isEditMode ? (
                  <>
                    <button
                      type="button"
                      className="profile-page__save-btn"
                      onClick={handleSaveChanges}
                      disabled={saving}
                    >
                      {saving ? <span className="profile-page__btn-saving">Saving…</span> : <SaveChangesIcon />}
                    </button>
                    <button
                      type="button"
                      className="profile-page__cancel-btn"
                      onClick={handleCancelEdit}
                    >
                      <CancelIcon />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="profile-page__edit-profile-btn"
                    onClick={enterEditMode}
                  >
                    <EditProfileIcon />
                  </button>
                )}
            </div>
          </section>

          {/* Bio */}
          <section className="profile-page__section profile-page__section--bio">
            <BioEditor value={profile.bio} onChange={handleBioChange} />
          </section>

          {/* Connected Platforms */}
          <section className="profile-page__section profile-page__section--platforms">
            <ConnectedPlatforms
              ref={connectedPlatformsRef}
              accounts={profile.connectedAccounts}
              onAddAccount={handleAddAccount}
              onRemoveAccount={handleRemoveAccount}
              editMode={isEditMode}
              onAccountsUpdate={async () => {
                try {
                  const data = await authApi.getProfile();
                  const profileData = data?.profile || {};
                  setProfile((prev) => ({
                    ...prev,
                    connectedAccounts: mapConnectedAccounts(profileData.connectedPlatforms),
                    oauthProvider: profileData.oauthProvider || prev.oauthProvider,
                    oauthProviderUsername: profileData.oauthProviderUsername || prev.oauthProviderUsername,
                  }));
                } catch { /* silent */ }
              }}
            />
          </section>

          {/* Media Visibility Toggle — edit mode only */}
          {isEditMode && (
            <section className="profile-page__section profile-page__section--media-visibility">
              <div className="profile-media-visibility">
                <div className="profile-media-visibility__copy">
                  <h3 className="profile-media-visibility__title">Media visibility</h3>
                  <p className="profile-media-visibility__hint">
                    {profile.mediaVisibility === 'followers'
                      ? 'Your approved media is visible to your followers.'
                      : 'Your approved media is visible only to you.'}
                  </p>
                </div>
                <label className="profile-media-visibility__switch">
                  <input
                    type="checkbox"
                    checked={profile.mediaVisibility === 'followers'}
                    onChange={(e) => handleMediaVisibilityChange(e.target.checked ? 'followers' : 'private')}
                  />
                  <span>{profile.mediaVisibility === 'followers' ? 'Followers' : 'Private'}</span>
                </label>
              </div>
            </section>
          )}

          {/* Media Gallery */}
          <section className="profile-page__section profile-page__section--media-gallery">
            <MediaGallery userId={profile.id} isOwnProfile isEditMode={isEditMode} />
          </section>
        </div>

        <Footer />
      </main>

      {/* Banner crop modal */}
      <Modal
        isOpen={isBannerModalOpen}
        onClose={handleBannerModalClose}
        title={showBannerCropper ? 'Adjust Banner' : 'Upload Banner Photo'}
        className="banner-upload__modal"
      >
        {showBannerCropper && tempBannerUrl ? (
          <ImageCropper
            imageUrl={tempBannerUrl}
            sourceFile={tempBannerFile}
            onComplete={handleBannerCropComplete}
            onCancel={handleBannerCropCancel}
            instructions="Scroll to zoom, drag to reposition"
            cropWidth={520}
            cropHeight={240}
            outputWidth={1920}
            outputHeight={886}
            shape="banner"
            initialFit="contain"
          />
        ) : (
          <FileUploadZone
            onFileSelect={handleBannerFileSelect}
            accept="image/*"
            prompt="Drop your banner here or click to upload"
            hint="Recommended size: 1920 x 886 pixels"
            icon={<span>🖼</span>}
          />
        )}
      </Modal>

      <FollowersPopup
        isOpen={showFollowersPopup}
        onClose={() => setShowFollowersPopup(false)}
        username={profile.username}
        initialTab={followersPopupTab}
      />
    </div>
  );
}

export default ProfilePage;
